import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Play, 
  Pause,
  FileText, 
  ChevronRight, 
  ShieldCheck, 
  Maximize2, 
  Volume2, 
  VolumeX, 
  Settings, 
  Download, 
  RotateCcw, 
  RotateCw, 
  ChevronLeft, 
  Loader2, 
  Info, 
  ExternalLink, 
  Shield 
} from 'lucide-react';
import { GlowingEffect } from '../ui/GlowingEffect';
import { cn } from '../../lib/utils';
import { OSContent } from '../../App';
import { supabase } from '../../lib/supabase';

interface BuyerViewProps {
  onExit: () => void;
  branding?: {
    creatorName: string;
    logoUrl: string;
    accentColor: string;
  };
  content: OSContent[];
  initialAssetId?: string;
}

export function BuyerView({ branding, content, onExit, initialAssetId }: BuyerViewProps) {
  const [activeAssetId, setActiveAssetId] = useState(initialAssetId || content[0]?.id || '');
  const [fingerprint, setFingerprint] = useState('');
  
  const activeAsset = content.find(a => a.id === activeAssetId);
  const accentColor = branding?.accentColor || '#3B82F6';

  useEffect(() => {
    const buyerId = Math.random().toString(36).substring(7).toUpperCase();
    setFingerprint(`SHA256:BUYER_${buyerId}`);
    
    const handleContext = (e: MouseEvent) => e.preventDefault();
    document.addEventListener('contextmenu', handleContext);
    return () => document.removeEventListener('contextmenu', handleContext);
  }, []);

  useEffect(() => {
    const recordAccess = async () => {
      if (!activeAsset || !fingerprint) return;

      const params = new URLSearchParams(window.location.search);
      const token = params.get('access') || params.get('token') || 'direct_access';
      
      try {
        const { error: logError } = await supabase.from('audit_logs').insert([{
          creator_id: activeAsset.creatorId,
          link_token: token,
          asset_name: activeAsset.name,
          status: 'Secure',
          fingerprint: fingerprint
        }]);

        if (logError) throw logError;

        if (token !== 'direct_access') {
          const { data: currentLink } = await supabase
            .from('links')
            .select('views')
            .eq('token', token)
            .single();
            
          if (currentLink) {
            await supabase
              .from('links')
              .update({ views: (currentLink.views || 0) + 1 })
              .eq('token', token);
          }
        }
      } catch (err) {
        console.warn("Security logging bypassed - possible connection issue:", err);
      }
    };

    recordAccess();
  }, [activeAssetId, fingerprint, activeAsset]);

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col font-light selection:bg-primary selection:text-primary-foreground">
      <div className="fixed bottom-10 right-10 z-[200] opacity-[0.03] pointer-events-none select-none font-mono">
         <span className="text-[14px] font-bold tracking-widest uppercase">{fingerprint}</span>
      </div>

      <header className="h-20 border-b border-border/10 px-8 flex items-center justify-between bg-background/50 backdrop-blur-xl z-50">
        <div className="flex items-center gap-6">
          <button onClick={onExit} className="p-2 hover:bg-muted/20 rounded-xl transition-colors group">
            <ChevronLeft className="size-5 text-muted-foreground group-hover:text-foreground transition-all" />
          </button>
          <div className="size-10 bg-muted/20 border border-border/40 rounded-xl flex items-center justify-center overflow-hidden">
            {branding?.logoUrl ? (
              <img src={branding.logoUrl} className="size-full object-contain" alt="Brand Logo" />
            ) : (
              <div className="size-4 rounded-full" style={{ background: accentColor }} />
            )}
          </div>
          <div className="h-8 w-px bg-border/10" />
          <h1 className="text-lg tracking-tight font-medium flex items-center gap-3">
            <span className="text-muted-foreground/60">{branding?.creatorName || 'Member Portal'}</span>
            <ChevronRight className="size-3 text-muted-foreground/20" />
            <span className="font-light truncate max-w-[200px]">{activeAsset?.name || 'Protected Content'}</span>
          </h1>
        </div>

        <div className="flex items-center gap-4">
          <div className="hidden md:flex items-center gap-3 px-4 py-2 rounded-xl bg-muted/10 border border-border/40">
            <ShieldCheck className="size-3" style={{ color: accentColor }} />
            <span className="text-[9px] font-bold tracking-widest uppercase">Verified Access: {fingerprint.slice(-8)}</span>
          </div>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        <main className="flex-1 overflow-y-auto bg-black/40 relative">
          <div className="h-full w-full flex flex-col p-8 md:p-12 lg:p-16">
            <AnimatePresence mode="wait">
              <motion.div 
                key={activeAsset?.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="flex-1 w-full"
              >
                {activeAsset?.type === 'Video' ? (
                  <HighEndVideoPlayer url={activeAsset.url} accentColor={accentColor} fingerprint={fingerprint} />
                ) : activeAsset?.type === 'PDF' ? (
                  <div className="h-full w-full flex flex-col items-center justify-center space-y-12">
                    <div className="w-full max-w-3xl aspect-[1/1.4] bg-card rounded-[48px] border border-border/40 p-16 relative overflow-hidden shadow-2xl">
                       <GlowingEffect spread={100} glow={true} disabled={false} proximity={0} inactiveZone={0} borderWidth={1} />
                       <div className="absolute inset-0 flex items-center justify-center opacity-[0.03] rotate-45 pointer-events-none select-none">
                          <p className="text-5xl font-black uppercase text-center leading-tight">AURE DRM PROTECTED<br/>{fingerprint}</p>
                       </div>
                       <div className="space-y-10 relative z-10">
                          <div className="flex items-center justify-between">
                            <div className="h-6 w-1/3 bg-muted/40 rounded-full" />
                            <Shield className="size-6 text-muted-foreground/20" />
                          </div>
                          <div className="space-y-6">
                            {[...Array(12)].map((_, i) => (
                              <div key={i} className="h-3 w-full bg-muted/20 rounded-full" style={{ width: `${80 + Math.random() * 20}%` }} />
                            ))}
                          </div>
                          <div className="h-32 w-full bg-muted/5 rounded-[32px] border border-border/10" />
                       </div>
                    </div>
                    <div className="relative rounded-3xl">
                       <GlowingEffect spread={30} glow={true} disabled={false} proximity={60} inactiveZone={0} borderWidth={1.5} />
                       <button className="relative px-10 py-4 rounded-3xl bg-muted/10 border border-border/40 text-[9px] font-bold tracking-[0.4em] uppercase opacity-40 cursor-not-allowed flex items-center gap-3" disabled>
                        <Download size={14} /> Security Lockdown: Downloads Restricted
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="max-w-4xl mx-auto w-full space-y-10 py-12">
                    <div className="p-16 bg-card/40 backdrop-blur-3xl rounded-[64px] border border-border/20 shadow-2xl relative overflow-hidden group">
                      <GlowingEffect spread={100} glow={true} disabled={false} proximity={0} inactiveZone={0} borderWidth={1} />
                      <div className="relative z-10 space-y-12">
                        <div className="flex items-center justify-between">
                           <div className="p-5 bg-muted/20 rounded-[28px] border border-border/10 text-primary">
                             <Info size={28} />
                           </div>
                        </div>
                        <div className="space-y-6">
                          <h2 className="text-5xl font-light tracking-tighter leading-tight">{activeAsset?.name}</h2>
                          <div className="h-px w-24" style={{ backgroundColor: accentColor }} />
                        </div>
                        <div className="prose prose-invert max-w-none">
                          <p className="text-xl text-muted-foreground/80 font-light leading-relaxed">
                            {activeAsset?.fileName || "Protected high-value information. Access to this document is limited to verified community members only."}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </main>

        <aside className="w-80 border-l border-border/10 bg-card/30 backdrop-blur-xl flex flex-col hidden lg:flex">
          <div className="p-10 border-b border-border/10">
             <div className="flex items-center justify-between mb-4 text-[10px] font-bold tracking-[0.5em] uppercase text-muted-foreground/40">
               <span>Module Progress</span>
               <span className="text-foreground">45%</span>
             </div>
             <div className="h-1.5 w-full bg-muted/10 rounded-full overflow-hidden">
               <motion.div 
                 className="h-full rounded-full" 
                 initial={{ width: 0 }}
                 animate={{ width: '45%' }}
                 style={{ backgroundColor: accentColor }} 
               />
             </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
            <h4 className="px-6 py-4 text-[9px] font-bold tracking-[0.4em] uppercase text-muted-foreground/40">Course Curriculum</h4>
            {content.length > 0 ? content.map((asset) => (
              <button 
                key={asset.id}
                onClick={() => setActiveAssetId(asset.id)}
                className={cn(
                  "w-full p-5 rounded-[32px] flex items-center gap-5 transition-all text-left group relative overflow-hidden", 
                  activeAssetId === asset.id ? "text-background shadow-xl" : "hover:bg-muted/10 text-muted-foreground"
                )}
                style={{ backgroundColor: activeAssetId === asset.id ? accentColor : 'transparent' }}
              >
                <div className={cn("size-10 rounded-2xl flex items-center justify-center transition-all relative z-10", activeAssetId === asset.id ? "bg-white/20" : "bg-muted/20 group-hover:bg-muted/40")}>
                  {asset.type === 'Video' ? <Play size={16} className={activeAssetId === asset.id ? 'fill-white' : ''} /> : asset.type === 'PDF' ? <FileText size={16} /> : <Info size={16} />}
                </div>
                <div className="flex-1 relative z-10">
                  <p className="text-[13px] font-medium truncate tracking-tight">{asset.name}</p>
                  <p className={cn("text-[9px] font-bold tracking-widest uppercase opacity-40", activeAssetId === asset.id ? "text-white" : "text-muted-foreground")}>
                    {asset.type} • Secured
                  </p>
                </div>
              </button>
            )) : (
              <div className="p-12 text-center text-muted-foreground/20 text-[10px] font-bold tracking-widest uppercase italic">The vault is empty.</div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function HighEndVideoPlayer({ url, accentColor, fingerprint }: { url?: string, accentColor: string, fingerprint: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [isMuted, setIsMuted] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) videoRef.current.pause();
    else videoRef.current.play();
    setIsPlaying(!isPlaying);
  };

  const skip = (amount: number) => {
    if (!videoRef.current) return;
    videoRef.current.currentTime += amount;
  };

  const handleProgress = () => {
    if (!videoRef.current) return;
    const progressValue = (videoRef.current.currentTime / videoRef.current.duration) * 100;
    setProgress(progressValue);
    setCurrentTime(videoRef.current.currentTime);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!videoRef.current) return;
    const seekTime = (parseFloat(e.target.value) / 100) * videoRef.current.duration;
    videoRef.current.currentTime = seekTime;
    setProgress(parseFloat(e.target.value));
    setCurrentTime(seekTime);
  };

  const formatTime = (time: number) => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  return (
    <div 
      ref={containerRef}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cn(
        "relative w-full h-full bg-black rounded-[56px] overflow-hidden shadow-[0_40px_100px_-20px_rgba(0,0,0,0.8)] group border border-border/10 transition-all duration-700",
        !isHovered && isPlaying ? "cursor-none" : "cursor-default"
      )}
    >
      <motion.div 
        animate={{ 
          x: [100, 300, 100], 
          y: [100, 200, 100],
          opacity: [0.01, 0.05, 0.01] 
        }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        className="absolute pointer-events-none select-none z-10 text-[10px] font-bold font-mono tracking-tighter text-white"
      >
        {fingerprint}
      </motion.div>

      <GlowingEffect spread={140} glow={isHovered} disabled={false} proximity={0} inactiveZone={0} borderWidth={1.5} />
      
      {isLoading && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-background/80 backdrop-blur-md">
          <div className="flex flex-col items-center gap-6">
             <Loader2 className="size-10 animate-spin text-primary opacity-20" />
             <p className="text-[10px] font-bold tracking-[0.5em] uppercase text-muted-foreground/30">Decrypting Stream...</p>
          </div>
        </div>
      )}

      <video
        ref={videoRef}
        src={url || "https://storage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4"}
        className="w-full h-full object-cover"
        onTimeUpdate={handleProgress}
        onLoadedMetadata={() => {
          setDuration(videoRef.current?.duration || 0);
          setIsLoading(false);
        }}
        onClick={togglePlay}
        playsInline
        disablePictureInPicture
        controlsList="nodownload"
      />

      <AnimatePresence>
        {(isHovered || !isPlaying) && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20 flex flex-col justify-end p-10 z-20"
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-12">
              <button onClick={() => skip(-10)} className="size-16 flex items-center justify-center rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition-all">
                <RotateCcw className="text-white size-6" />
              </button>
              <button onClick={togglePlay} className="size-28 rounded-full bg-white flex items-center justify-center shadow-2xl">
                {isPlaying ? <Pause className="text-black size-12 fill-black" /> : <Play className="text-black size-12 fill-black translate-x-1.5" />}
              </button>
              <button onClick={() => skip(10)} className="size-16 flex items-center justify-center rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition-all">
                <RotateCw className="text-white size-6" />
              </button>
            </div>

            <div className="space-y-8 max-w-5xl mx-auto w-full">
              <div className="space-y-4">
                <div className="relative flex items-center w-full h-1.5">
                  <div className="absolute w-full h-full bg-white/10 rounded-full overflow-hidden">
                    <motion.div className="h-full rounded-full" style={{ width: `${progress}%`, backgroundColor: accentColor }} />
                  </div>
                  <input type="range" min="0" max="100" step="0.1" value={progress} onChange={handleSeek} className="absolute w-full h-6 appearance-none bg-transparent cursor-pointer z-10" />
                </div>
                <div className="flex justify-between text-[10px] font-bold tracking-[0.3em] font-mono text-white/40 uppercase">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-12">
                  <button onClick={() => setIsMuted(!isMuted)} className="text-white/60 hover:text-white transition-colors">
                    {isMuted || volume === 0 ? <VolumeX size={20} /> : <Volume2 size={20} />}
                  </button>
                  <p className="text-[10px] font-bold tracking-[0.4em] uppercase text-white/20">Aure Encryption Protocol Active</p>
                </div>
                <div className="flex items-center gap-8">
                   <Settings size={20} className="text-white/40" />
                   <Maximize2 size={20} className="text-white/40" />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}